/**
 * AppShell — desktop layout: full-height sidebar | feed (Outlet) | right banner.
 * Public: anonymous visitors can browse; auth-only actions are gated.
 */
import type { PropsWithChildren } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { ProfileProvider } from '@wlb/shared/context/ProfileContext';
import { GroupProvider } from '@wlb/shared/context/GroupContext';
import { CreatePostProvider } from '@wlb/shared/context/CreatePostContext';
import { MessageProvider } from '@wlb/shared/context/MessageContext';
import { CreatePostFlowProvider } from '@wlb/shared/context/CreatePostFlowContext';
import { MessagesBackgroundSync } from '../../features/messages/MessagesBackgroundSync';
import { NotificationBadgeProvider } from '../../features/notifications/NotificationBadgeContext';
import { MandatoryFirstPostGate } from '../../features/createPost/MandatoryFirstPostGate';
import { SideNav } from './SideNav';
import { MobileTopBar } from './MobileTopBar';
import { RightBanner } from './RightBanner';
import './layout.css';

/**
 * ProfileProvider fetches GET /UserProfile/me on mount, so it stays out of the
 * tree for anonymous visitors — a 401 there would raise the session-expired
 * dialog on the public landing page.
 *
 * MessageProvider defers its conversation list so signing in does not spend a
 * request before anything reads it. The nav unread badge does read it, but it
 * is refreshed by `useConversationBadgeSync` on the navigations that can change
 * it, which covers the first load too — see `MessagesBackgroundSync`.
 */
function MemberProfileProvider({ children }: PropsWithChildren) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? (
    <ProfileProvider>
      <GroupProvider>
        <CreatePostProvider>
          <CreatePostFlowProvider>
            <MessageProvider deferInitialFetch>
              <NotificationBadgeProvider>
                <MessagesBackgroundSync />
                <MandatoryFirstPostGate>{children}</MandatoryFirstPostGate>
              </NotificationBadgeProvider>
            </MessageProvider>
          </CreatePostFlowProvider>
        </CreatePostProvider>
      </GroupProvider>
    </ProfileProvider>
  ) : <>{children}</>;
}

export function AppShell() {
  return (
    <MemberProfileProvider>
      <div className="shell">
        <MobileTopBar />
        <SideNav />
        <main className="shell__content">
          <Outlet />
        </main>
        <RightBanner />
      </div>
    </MemberProfileProvider>
  );
}
